import React, { useMemo, useEffect, useState } from 'react'
import { Alert, Button } from 'antd'
import { ClockCircleOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'

import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'

import IntlMessages from '../../util/IntlMessages'

import { FrontendUrls } from '../../types/urls'

import '../../styles/components/trial-banner.less'

interface TrialBannerProps {
  className?: string
  sidebarCollapsed?: boolean
}

const TrialBanner: React.FC<TrialBannerProps> = ({ className = '', sidebarCollapsed = false }) => {
  const history = useHistory()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const [currentTime, setCurrentTime] = useState(dayjs())

  // Update current time daily (handles sleep, tab switching, etc.)
  useEffect(() => {
    // Initial setup
    const updateTime = () => setCurrentTime(dayjs())

    // Update every minute to catch day changes quickly
    const intervalId = setInterval(updateTime, 60 * 1000) // 1 minute

    // Handle page visibility changes (tab focus, wake from sleep)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateTime() // Immediately update when tab becomes visible
      }
    }

    // Handle window focus (additional safety)
    const handleFocus = () => updateTime()

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    // Cleanup
    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const trialInfo = useMemo(() => {
    if (!authCompany || authCompany.subscriptionStatus !== 'trialing') {
      return null
    }

    let endDate: dayjs.Dayjs | null = null
    let planName = 'Complete'

    // Handle Stripe billing
    if (authCompany.billing?.stripeCurrentPeriodEnd && authCompany.billing?.paymentProcessor !== 'microsoft-billing') {
      endDate = dayjs.unix(Number(authCompany.billing.stripeCurrentPeriodEnd))
    }
    // Handle Microsoft billing
    else if (authCompany.billing?.msPeriodEndDate && authCompany.billing?.paymentProcessor === 'microsoft-billing') {
      endDate = dayjs(authCompany.billing.msPeriodEndDate)
    }
    // Fallback: Calculate trial end date based on company creation timestamp
    // This handles cases where billing information is not yet available after signup
    else if (authCompany.timestamp) {
      // Trial duration is 7 days as defined in backend (TRIAL_DURATION_DAYS = 7)
      endDate = dayjs(authCompany.timestamp).add(7, 'days')
    }

    if (!endDate) {
      return null
    }

    // Get plan name from billing info or fallback to company plan
    if (authCompany.billing?.subscriptionPlan) {
      planName = authCompany.billing.subscriptionPlan
    } else if (authCompany.plan) {
      planName = authCompany.plan
    }

    // Calculate days remaining - use raw difference to match trial day number
    // Today (Friday) = 5th day → should show "5 days left"
    const rawDaysLeft = endDate.diff(currentTime, 'days')
    const adjustedDaysLeft = rawDaysLeft >= 0 ? rawDaysLeft + 1 : 0

    // Don't show banner if trial has expired (0 or negative days)
    if (adjustedDaysLeft <= 0) {
      return null
    }

    return {
      daysLeft: adjustedDaysLeft,
      planName,
      endDate,
    }
  }, [authCompany, currentTime, authCompany?.billing, authUser.role])

  const handleAddBillingClick = () => {
    history.push(FrontendUrls.billing)
  }

  if (!trialInfo) {
    return null
  }

  const { daysLeft, planName } = trialInfo

  // Determine alert type based on urgency
  const getAlertType = (days: number): 'info' | 'warning' | 'error' => {
    if (days <= 2) return 'error'
    if (days <= 5) return 'warning'
    return 'info'
  }

  const alertType = getAlertType(daysLeft)

  // Format the message with proper i18n pluralization
  const formatMessage = (days: number, plan: string) => {
    return <IntlMessages id="app.trialInfoBanner" values={{ days, plan }} />
  }

  const message = formatMessage(daysLeft, planName)

  return (
    <div className={`trial-banner ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'} ${className}`}>
      <Alert
        type={alertType}
        showIcon
        icon={<ClockCircleOutlined />}
        message={message}
        action={
          authUser.role === 'Admin' && (
            <Button size="small" type="link" onClick={handleAddBillingClick} className="trial-banner-action" aria-label="Add billing information">
              <IntlMessages id="app.trialInfoBillingLink" />
            </Button>
          )
        }
        closable={false}
        className="trial-banner-alert"
        role="banner"
        aria-live="polite"
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        aria-label={`Trial status: ${message}`}
      />
    </div>
  )
}

export default React.memo(TrialBanner)
